<script setup>
import { ref, computed, onMounted } from 'vue';
import { useWidgets } from '@store/widgets';
import { useRoute, useRouter } from 'vue-router';
import { Group, Tabs, InputLabel, InputGroup, IconUploader, Text, Button, Select, Toggle, Tooltip, Textarea, ColorPicker, ProBadge } from '@components';
import { useTranslation } from '../../composables/useTranslation';

const { t } = useTranslation();

const $widget = useWidgets();
const greetings = computed( () => $widget.currentWidget.config?.greetings || {});

const simple = computed( () => $widget.currentWidget.config?.greetings?.templates?.simple || {});
const wave = computed( () => $widget.currentWidget.config?.greetings?.templates?.wave || {});

const sizes = computed(() => ({ 
    small: t('small'), 
    medium: t('medium'), 
    large: t('large'), 
    custom: t('custom') 
}));

const isLocked = computed ( () => !formychat_admin_vars.is_premium )

const templateLabel = (template) => {
    const labels = { simple: t('greeting_template_simple'), wave: t('greeting_template_wave'), whatsapp: t('greeting_template_whatsapp') };
    return labels[template] || template;
}

const setStyle = ( styleId ) => {

    if ( styleId === 2 & isLocked.value ) return;

    greetings.value.style = styleId;

    if ( greetings.value.template === 'wave' ) {
        wave.value.icon_position = styleId === 1 ? 'before_heading' : 'before_cta';
    } 
}
</script>
<template>
    <div class="w-full h-full space-y-6">
        <InputGroup>
            <Toggle v-model="greetings.enabled" :label="t('enable_greetings')">{{ t('display_greeting_popup') }} </Toggle>
        </InputGroup>

        <InputGroup class="w-full h-full space-y-6" :condition="greetings.enabled == true">

            <InputGroup>
                <InputLabel>{{ t('choose_greetings_template') }}</InputLabel>

                <div class="flex items-center gap-5 flex-wrap child:w-40">
                    <div class="flex flex-col gap-2 group cursor-pointer" v-for="template in ['simple', 'wave', 'whatsapp']" @click.prevent="greetings.template = template == 'whatsapp' ? greetings.template : template">
                        <div class="relative ring-[2.5px] ring-gray-200  rounded h-40 w-ful flex items-center justify-center transition"
                        :class="{'group-hover:ring-indigo-500' : template !== 'whatsapp', 'ring-indigo-500' : greetings.template == template}">
                            <img class="w-1/2" :src="Image(`greetings/styles/${template}${template === 'whatsapp' ? '' : '-1'}.png`)">
                            
                            <div v-if="template === 'whatsapp'" class="rounded group-hover:flex hidden justify-center items-center absolute left-0 top-0 w-full h-full bounceIn">
                                <div class="bg-white absolute left-0 top-0 w-full h-full rounded z-0 opacity-50"></div>
                                <span class="bg-indigo-600 px-2 h-6 text-[12px] font-medium flex items-center justify-center rounded-2xl text-white z-10">{{ t('upcoming') }}</span>
                            </div>

                            <transition name="bounce">
                                <svg v-if="greetings.template == template" xmlns="http://www.w3.org/2000/svg" class="fill-indigo-500 w-4 absolute right-2 top-2" viewBox="0 0 16 16">
                                    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                                </svg>
                            </transition>
                        </div>
                        <span class="group-hover:font-medium text-gray-700 text-sm tracking-wide text-center capitalize">{{ templateLabel(template) }}</span>
                    </div>
                </div>
            </InputGroup>

            <InputGroup>
                <div class="flex items-center gap-5 flex-wrap child:w-24">
                    <div class="flex flex-col gap-2 group cursor-pointer" v-for="styleId in [1, 2]" @click="setStyle( styleId )">
                        <div class="relative overflow-hidden ring-[2.5px] ring-gray-200 rounded group h-24 w-ful flex items-center justify-center transition group-hover:ring-indigo-500"
                        :class="{'ring-indigo-500' : greetings.style == styleId, 'formychat-locked' : styleId === 2 && isLocked}">
                            <img class="w-2/3 h-auto" :src="Image(`greetings/styles/${greetings.template}-${styleId}.png`)">

                            <div v-if="styleId === 2 && isLocked" class="rounded group-hover:flex hidden justify-center items-center absolute left-0 top-0 w-full h-full bounceIn">
                                <div class="bg-white absolute left-0 top-0 w-full h-full rounded z-0 opacity-50"></div>
                                <ProBadge />
                            </div>
                            
                            <transition name="bounce">
                                <svg v-if="greetings.style == styleId" xmlns="http://www.w3.org/2000/svg" class="fill-indigo-500 w-4 absolute right-2 top-2" viewBox="0 0 16 16">
                                    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                                </svg>
                            </transition>
                        </div>
                        <span class="group-hover:font-medium text-gray-700 text-sm tracking-wide text-center capitalize">{{ t('style_number', { number: styleId }) }}</span>
                    </div>
                </div>
            </InputGroup>

            <Group :title="t('customize_greetings')" :gap="false">

                <!-- Simple  -->
                <div class="space-y-6" v-if="greetings.template === 'simple'">
                    <div class="bg-slate-50 p-5 space-y-5 rounded border border-gray-100">
                        <InputGroup>
                            <InputLabel>{{ t('greeting_heading') }}</InputLabel>
                            <Textarea v-model="simple.heading" :placeholder="t('hi_any_queries_placeholder')" :white="true"></Textarea>
                        </InputGroup>
            
                        <InputGroup>
                            <InputLabel>{{ t('heading_size') }}</InputLabel>
                            <Tabs :options="sizes" v-model="simple.heading_size" :locked="['custom']"></Tabs>
                        </InputGroup>
                        
                        <InputGroup :condition="simple.heading_size == 'custom'">
                            <InputLabel :hints="t('select_icon_size_hint')"> {{ t('custom_heading_size') }} </InputLabel>
                            <div class="w-full flex items-center justify-between gap-2">
                                <input class="formychat-slider" type="range" v-model="simple.heading_size_custom" min="8" max="72">
                                <span class="w-16">{{ simple.heading_size_custom }}px</span>
                            </div>
                        </InputGroup>
                    </div>
    
                    <div class="bg-slate-50 p-5 space-y-5 rounded border border-gray-100">
                        <InputGroup>
                            <InputLabel>{{ t('greeting_message') }}</InputLabel>
                            <Textarea v-model="simple.message" :placeholder="t('hi_any_queries_placeholder')" :white="true"></Textarea>
                        </InputGroup>
            
                        <InputGroup>
                            <InputLabel>{{ t('message_size') }}</InputLabel>
                            <Tabs :options="sizes" v-model="simple.message_size" :locked="['custom']"></Tabs>
                        </InputGroup>

                        <InputGroup :condition="simple.message_size == 'custom'">
                            <InputLabel :hints="t('select_icon_size_hint')"> {{ t('custom_message_size') }} </InputLabel>
                            <div class="w-full flex items-center justify-between gap-2">
                                <input class="formychat-slider" type="range" v-model="simple.message_size_custom" min="8" max="72">
                                <span class="w-16">{{ simple.message_size_custom }}px</span>
                            </div>
                        </InputGroup>

                    </div>
    
                    <div class="bg-slate-50 p-5 space-y-5 rounded border border-gray-100">
                        <InputGroup>
                            <InputLabel locked>{{ t('greeting_colors') }}</InputLabel>
                            <div class="flex items-center gap-4 flex-wrap" :class="{'formychat-locked opacity-60 child:pointer-events-none' : isLocked}">
                                <ColorPicker v-model="simple.heading_color">{{ t('heading_color') }}</ColorPicker>
                                <ColorPicker v-model="simple.message_color">{{ t('message_color') }}</ColorPicker>
                                <ColorPicker v-model="simple.background_color">{{ t('background_color') }}</ColorPicker>
                            </div>
                        </InputGroup>
            
                        <InputGroup>
                            <InputLabel>{{ t('greetings_font_family') }}</InputLabel>
                            <Select v-model="simple.font_family" :options="$widget.contents.fonts" class="w-48"></Select>
                        </InputGroup>
                    </div>
                </div>
                
                <!-- Wave  -->
                <div class="space-y-6"  v-if="greetings.template === 'wave'">
                    <div class="bg-slate-50 p-5 space-y-5 rounded border border-gray-100">
                        <InputGroup class="space-y-3">
                            <Toggle v-model="wave.show_icon">{{ t('show_main_content') }}</Toggle>
                        </InputGroup>

                        <InputGroup :condition="wave.show_icon">
                            <IconUploader v-if="wave.show_icon" locked v-model="wave.icon_url" v-model:custom="wave.icon_url_custom" :default="Image('greetings/hand-wave.svg')" />
                        </InputGroup>

                        <InputGroup :condition="wave.show_icon">
                            <InputLabel>{{ t('position') }}</InputLabel>
                            <Tabs width="135px" :options="greetings.style === 1 ? { before_heading: t('before_heading'), after_heading: t('after_heading'), after_message : t('after_message') } : { before_cta : t('before_cta'), after_cta : t('after_cta') }" :locked="['after_heading', 'after_message']" v-model="wave.icon_position"></Tabs>
                        </InputGroup>
                    </div>

                    <!-- Style 1  -->
                    <div v-if="greetings.style === 1" class="space-y-5">
                        <div class="bg-slate-50 p-5 space-y-5 rounded border border-gray-100">
                            <InputGroup>
                                <InputLabel>{{ t('greeting_heading') }}</InputLabel>
                                <Textarea v-model="wave.heading" :placeholder="t('hi_any_queries_placeholder')" :white="true"></Textarea>
                            </InputGroup>
                
                            <InputGroup>
                                <InputLabel>{{ t('heading_size') }}</InputLabel>
                                <Tabs :options="sizes" v-model="wave.heading_size"></Tabs>
                            </InputGroup>

                            <InputGroup :condition="wave.heading_size == 'custom'">
                                <InputLabel :hints="t('select_icon_size_hint')"> {{ t('custom_heading_size') }} </InputLabel>
                                <div class="w-full flex items-center justify-between gap-2">
                                    <input class="formychat-slider" type="range" v-model="wave.heading_size_custom" min="20" max="150">
                                    <span class="w-16">{{ wave.heading_size_custom }}px</span>
                                </div>
                            </InputGroup>
                        </div>
        
                        <div class="bg-slate-50 p-5 space-y-5 rounded border border-gray-100">
                            <InputGroup>
                                <InputLabel>{{ t('greeting_message') }}</InputLabel>
                                <Textarea v-model="wave.message" :placeholder="t('hi_any_queries_placeholder')" :white="true"></Textarea>
                            </InputGroup>
                
                            <InputGroup>
                                <InputLabel>{{ t('message_size') }}</InputLabel>
                                <Tabs :options="sizes" v-model="wave.message_size"></Tabs>
                            </InputGroup>

                            <InputGroup :condition="wave.message_size == 'custom'">
                                <InputLabel :hints="t('select_icon_size_hint')"> {{ t('custom_message_size') }} </InputLabel>
                                <div class="w-full flex items-center justify-between gap-2">
                                    <input class="formychat-slider" type="range" v-model="wave.message_size_custom" min="20" max="150">
                                    <span class="w-16">{{ wave.message_size_custom }}px</span>
                                </div>
                            </InputGroup>
                        </div>
        
                        <div class="bg-slate-50 p-5 space-y-5 rounded border border-gray-100">
                            <InputGroup>
                                <InputLabel locked>{{ t('greeting_colors') }}</InputLabel>
                                <div class="flex items-center gap-4"  :class="{'formychat-locked opacity-60 child:pointer-events-none' : isLocked}">
                                    <ColorPicker v-model="wave.heading_color">{{ t('heading_color') }}</ColorPicker>
                                    <ColorPicker v-model="wave.message_color">{{ t('message_color') }}</ColorPicker>
                                    <ColorPicker v-model="wave.background_color">{{ t('background_color') }}</ColorPicker>
                                </div>
                            </InputGroup>
                
                            <InputGroup>
                                <InputLabel>{{ t('greetings_font_family') }}</InputLabel>
                                <Select v-model="wave.font_family" :options="$widget.contents.fonts" class="w-48"></Select>
                            </InputGroup>
                        </div>

                        <!-- CTA  -->
                        <div class="bg-slate-50 p-5 space-y-5 rounded border border-gray-100">
                            <InputGroup class="space-y-3">
                                <Toggle v-model="wave.show_cta">{{ t('show_greetings_cta') }}</Toggle>
                            </InputGroup>

                            <InputGroup :condition="wave.show_cta">
                                <InputLabel>{{ t('greeting_cta_text') }}</InputLabel>
                                <Textarea v-model="wave.cta_text" :placeholder="t('hi_any_queries_placeholder')" :white="true"></Textarea>
                            </InputGroup>

                            <InputGroup class="space-y-3" :condition="wave.show_cta">
                                <InputLabel>{{ t('cta_icon') }} <code>png</code>, <code>jpg</code>, <code>svg</code></InputLabel>
                                <IconUploader locked v-model="wave.cta_icon_url" v-model:custom="wave.cta_icon_url_custom" :default="Image('greetings/vector.svg')" />
                            </InputGroup>
                
                            <InputGroup :condition="wave.show_cta">
                                <InputLabel locked>{{ t('cta_style') }}</InputLabel>
                                <div class="flex items-center gap-4" :class="{'formychat-locked opacity-60 child:pointer-events-none' : isLocked}">
                                    <ColorPicker v-model="wave.cta_text_color">{{ t('text_color') }}</ColorPicker>
                                    <ColorPicker v-model="wave.cta_background_color">{{ t('background_color') }}</ColorPicker>
                                </div>
                            </InputGroup>
                
                        </div>
                    </div>

                    <!-- Style 2 -->
                    <div v-if="greetings.style === 2" class="space-y-5">
                        <div class="bg-slate-50 p-5 space-y-5 rounded border border-gray-100">

                            <InputGroup>
                                <Toggle v-model="wave.show_cta">{{ t('show_greeting_cta') }}</Toggle>
                            </InputGroup>

                            <InputGroup :condition="wave.show_cta">
                                <InputLabel>{{ t('greeting_cta_heading') }}</InputLabel>
                                <Textarea v-model="wave.cta_heading" :placeholder="t('hi_any_queries_placeholder')" :white="true"></Textarea>
                            </InputGroup>
                
                            <InputGroup :condition="wave.show_cta">
                                <InputLabel>{{ t('cta_heading_size') }}</InputLabel>
                                <Tabs :options="sizes" v-model="wave.cta_heading_size"></Tabs>
                            </InputGroup>

                            <InputGroup :condition="wave.show_cta && wave.cta_heading_size == 'custom'">
                                <InputLabel :hints="t('select_icon_size_hint')"> {{ t('custom_cta_heading_size') }} </InputLabel>
                                <div class="w-full flex items-center justify-between gap-2">
                                    <input class="formychat-slider" type="range" v-model="wave.cta_heading_size_custom" min="20" max="150">
                                    <span class="w-16">{{ wave.cta_heading_size_custom }}px</span>
                                </div>
                            </InputGroup>

                            <InputGroup :condition="wave.show_cta">
                                <InputLabel>{{ t('greeting_cta_message') }}</InputLabel>
                                <Textarea v-model="wave.cta_message" :placeholder="t('hi_any_queries_placeholder')" :white="true"></Textarea>
                            </InputGroup>
                
                            <InputGroup :condition="wave.show_cta">
                                <InputLabel>{{ t('cta_message_size') }}</InputLabel>
                                <Tabs :options="sizes" v-model="wave.cta_message_size"></Tabs>
                            </InputGroup>

                            <InputGroup :condition="wave.show_cta && wave.cta_message_size == 'custom'">
                                <InputLabel :hints="t('select_icon_size_hint')"> {{ t('custom_cta_message_size') }} </InputLabel>
                                <div class="w-full flex items-center justify-between gap-2">
                                    <input class="formychat-slider" type="range" v-model="wave.cta_message_size_custom" min="20" max="150">
                                    <span class="w-16">{{ wave.cta_message_size_custom }}px</span>
                                </div>
                            </InputGroup>

                        </div>

                        
                        <transition name="slide">
                            <div v-if="wave.show_cta" class="bg-slate-50 p-5 space-y-5 rounded border border-gray-100">
                                <InputGroup>
                                    <InputLabel>{{ t('cta_colors') }}</InputLabel>
                                    <div class="flex items-center gap-4">
                                        <ColorPicker v-model="wave.cta_heading_color">{{ t('heading_color') }}</ColorPicker>
                                        <ColorPicker v-model="wave.cta_message_color">{{ t('message_color') }}</ColorPicker>
                                        <ColorPicker v-model="wave.cta_background_color">{{ t('background_color') }}</ColorPicker>
                                    </div>
                                </InputGroup>

                                <InputGroup>
                                    <InputLabel>{{ t('greeting_font_family') }}</InputLabel>
                                    <Select v-model="wave.font_family" :options="$widget.contents.fonts" class="w-48"></Select>
                                </InputGroup>
                            </div>
                        </transition>
                    </div>
                </div>
            </Group>

            <Group :title="t('greeting_behavior')" :gap="false">
                <InputLabel>{{ t('on_click_action') }}</InputLabel>
                <div class="flex flex-col gap-2.5 child:cursor-pointer">
                    <label class="flex items-center gap-4 group" 
                    v-for="(label, action) in { show_form: t('load_selected_form'), redirect : t('redirect_whatsapp_directly') }"
                    @click.prevent="(isLocked && action == 'redirect') ? null : greetings.on_click = action"
                    :class="{ 'text-indigo-500' : greetings.on_click == action, }"
                    > 
                        <div class="w-5 h-5 bg-white border-[5px] rounded-full  transition" :class="{ 
                            'border-indigo-500' : greetings.on_click == action, 
                            'border-gray-100' : greetings.on_click !== action,
                            'group-hover:border-indigo-500' : greetings.on_click !== action && !isLocked,
                            'formychat-locked' : isLocked && action === 'redirect'
                        }"><span></span></div> <span :class="{
                            'opacity-60 formychat-locked' : isLocked && action === 'redirect'}">{{ label }}</span>
                        <ProBadge v-if="action === 'redirect'" />
                    </label>
                </div>
                
            </Group>
        </InputGroup>
    </div>
</template>
